import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Empty,
  Input,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useApp } from "../../contexts/AppContext";
import { referenceApi, skillEffectRelationApi } from "../../services/api";
import { raidAuthoringApi } from "../../services/raidAuthoringApi";
import { readReference, skillReferences } from "../../utils/raidAuthoring";
import { seconds } from "../../utils/resourcePresentation";
import "./ResourceTables.css";
const { Text, Title } = Typography;

const RelationsTab: React.FC<{ bossCode?: string; entityCode?: string }> = ({
  bossCode,
  entityCode,
}) => {
  const { state, setActiveTab } = useApp();
  const code =
    entityCode ||
    bossCode ||
    state.selectedEntity?.entity_code ||
    state.selectedBoss?.boss_code;
  const [data, setData] = useState<any>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false);
  const [owner, setOwner] = useState("all"),
    [mode, setMode] = useState("skill"),
    [search, setSearch] = useState(""),
    [selected, setSelected] = useState("");
  const request = useRef(0);
  const load = useCallback(async () => {
    if (!code) return;
    const id = ++request.current;
    setLoading(true);
    setError("");
    try {
      const [skills, effects, entities, relations, events, actions] =
        await Promise.all([
          referenceApi.fetchSkills(),
          referenceApi.fetchEffects(),
          referenceApi.fetchEntities(),
          skillEffectRelationApi.getAll(),
          referenceApi.fetchEventTypes(),
          referenceApi.fetchActionTypes(),
        ]);
      const codes = [
        code,
        ...(entityCode
          ? []
          : entities
              .filter((e) => e.parent_code === code)
              .map((e) => e.entity_code)),
      ];
      const links = await raidAuthoringApi.triggers(codes);
      const used = new Set(
        links.triggers.flatMap((t) =>
          skillReferences(
            t,
            links.actions.filter((a) => a.trigger_id === t.id),
          ),
        ),
      );
      const own = skills.filter(
        (s) => codes.includes(s.boss_code) || used.has(s.key),
      );
      const missingSkills = Array.from(used).filter(
        (key) => !skills.some((s) => s.key === key),
      );
      const relevant = relations.filter(
        (r) =>
          own.some((s) => s.key === r.skill) || missingSkills.includes(r.skill),
      );
      const effectKeys = [
        ...relevant.map((r) => r.effect),
        ...links.actions.map((a) => readReference(a.action_params, "effect")),
        ...links.triggers
          .filter((t) => t.event_type?.startsWith("ON_EFFECT_"))
          .map((t) => readReference(t.event_params, "effect")),
      ].filter(Boolean);
      const missing = [
        ...missingSkills.map((key) => "스킬: " + key),
        ...effectKeys
          .filter((key) => !effects.some((e) => e.key === key))
          .map((key) => "효과: " + key),
      ];
      if (id !== request.current) return;
      setData({
        skills,
        effects,
        entities,
        codes,
        own: [
          ...own,
          ...missingSkills.map((key) => ({
            key,
            name: "연결 확인 필요",
            boss_code: code,
            _missing: true,
          })),
        ],
        relations: relevant,
        ...links,
        events,
        actionTypes: actions,
        missing: Array.from(new Set(missing)),
      });
    } catch (e: any) {
      if (id === request.current)
        setError(e.message || "연결 데이터를 불러오지 못했습니다.");
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [code, entityCode]);
  useEffect(() => {
    setData(null);
    setOwner("all");
    setSelected("");
    setSearch("");
    load();
    const refresh = () => {
      load();
    };
    const events = ["triggersUpdated", "skillsUpdated", "effectsUpdated"];
    events.forEach((e) => window.addEventListener(e, refresh));
    return () => {
      request.current++;
      events.forEach((e) => window.removeEventListener(e, refresh));
    };
  }, [load]);
  const entityName = (value: string) =>
    data?.entities.find((e: any) => e.entity_code === value)?.entity_name ||
    value;
  const refName = (kind: "skill" | "effect", key?: string) =>
    key
      ? data?.[kind === "skill" ? "skills" : "effects"].find(
          (r: any) => r.key === key,
        )?.name || "연결 확인 필요"
      : "지정 없음";
  const reference = (kind: "skill" | "effect", key?: string) => (
    <span title={key}>{refName(kind, key)}</span>
  );
  const filteredSkills = (data?.own || []).filter(
    (s: any) =>
      (owner === "all" || s.boss_code === owner) &&
      [s.name, s.key, entityName(s.boss_code)].some((v) =>
        v?.toLowerCase().includes(search.toLowerCase()),
      ),
  );
  const activeSkill =
    filteredSkills.find((s: any) => s.key === selected) || filteredSkills[0];
  const relatedTriggers = (data?.triggers || []).filter((t: any) =>
    mode === "skill"
      ? activeSkill &&
        skillReferences(
          t,
          data.actions.filter((a: any) => a.trigger_id === t.id),
        ).includes(activeSkill.key)
      : (owner === "all" || t.entity_code === owner) &&
        [t.trigger_name, t.trigger_code, t.event_type].some((v) =>
          v?.toLowerCase().includes(search.toLowerCase()),
        ),
  );
  const relatedEffects = (data?.relations || [])
    .filter((r: any) => r.skill === activeSkill?.key)
    .slice()
    .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
  const triggerDetails = (t: any) => {
    const actions = data.actions
      .filter((a: any) => a.trigger_id === t.id)
      .slice()
      .sort((a: any, b: any) => (a.action_order ?? 0) - (b.action_order ?? 0));
    return (
      <div className="resource-details">
        <p>{t.description || "설명 없음"}</p>
        <Table
          size="small"
          pagination={false}
          rowKey={(a: any) => a.id}
          dataSource={actions}
          scroll={{ x: 650 }}
          columns={[
            { title: "순서", dataIndex: "action_order", width: 65 },
            {
              title: "실행",
              dataIndex: "action_type",
              width: 170,
              render: (v: string) =>
                data.actionTypes.find((a: any) => a.action_code === v)
                  ?.action_name || v,
            },
            {
              title: "내용",
              render: (_: any, a: any) => (
                <Space direction="vertical" size={2}>
                  {skillReferences({}, [a]).map((key) => (
                    <Text key={key}>{reference("skill", key)}</Text>
                  ))}
                  {readReference(a.action_params, "effect") &&
                    reference(
                      "effect",
                      readReference(a.action_params, "effect"),
                    )}
                  {a.action_type === "SHOW_MESSAGE" && (
                    <Text>
                      {a.action_params?.type === "WARNING" ? "경고" : "메시지"}:{" "}
                      {a.action_params?.message}
                    </Text>
                  )}
                  {a.action_type === "SPAWN_ENTITY" && (
                    <Text>
                      소환: {entityName(a.action_params?.entity_code)}
                    </Text>
                  )}
                  <Text type="secondary">
                    대상: {a.target_role || "지정 없음"}
                  </Text>
                </Space>
              ),
            },
            {
              title: "실행 시점",
              width: 110,
              render: (_: any, a: any) => seconds(a.delay_seconds ?? 0),
            },
          ]}
        />
        <Collapse
          ghost
          items={[
            {
              key: "raw",
              label: "코드 · 원본 매개변수",
              children: (
                <pre className="resource-json">
                  {JSON.stringify({ trigger: t, actions }, null, 2)}
                </pre>
              ),
            },
          ]}
        />
      </div>
    );
  };
  return (
    <div className="resource-page">
      <div className="resource-toolbar">
        <div>
          <Title level={4}>연결 관계</Title>
          <Text type="secondary">
            스킬을 선택해 적용 효과와 관련 트리거를 확인하세요.
          </Text>
        </div>
        <Space>
          <Button onClick={load} loading={loading}>
            새로고침
          </Button>
          <Button onClick={() => setActiveTab("triggers-v3")}>
            트리거 편집으로 이동
          </Button>
        </Space>
      </div>
      {error && <Alert type="error" showIcon message={error} />}
      <Space wrap className="resource-filters">
        <Radio.Group
          value={mode}
          onChange={(e) => {
            setMode(e.target.value);
            setSearch("");
          }}
          optionType="button"
          options={[
            { value: "skill", label: "스킬별 연결" },
            { value: "trigger", label: "몬스터 트리거" },
          ]}
        />
        <Select
          aria-label="연결 몬스터"
          value={owner}
          onChange={setOwner}
          style={{ minWidth: 210 }}
          options={[
            { value: "all", label: "전체 몬스터" },
            ...Array.from(
              new Set<string>([
                ...(data?.codes || []),
                ...(data?.own || []).map((s: any) => s.boss_code),
              ]),
            ).map((value) => ({ value, label: entityName(value) })),
          ]}
        />
        <Input.Search
          aria-label="연결 검색"
          allowClear
          placeholder={
            mode === "skill"
              ? "스킬 이름 또는 코드 검색"
              : "트리거 이름 또는 코드 검색"
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
      </Space>
      {data?.missing.length > 0 && (
        <Collapse
          style={{ marginBottom: 20 }}
          items={[
            {
              key: "missing",
              label: (
                <Text type="warning">
                  연결 확인 필요 {data.missing.length}개
                </Text>
              ),
              children: (
                <Space direction="vertical">
                  {data.missing.map((v: string) => (
                    <Text key={v} code>
                      {v}
                    </Text>
                  ))}
                  <Text type="secondary">원본 연결을 유지한 상태입니다.</Text>
                </Space>
              ),
            },
          ]}
        />
      )}
      {mode === "skill" && (
        <>
          <Select
            aria-label="연결 스킬"
            value={activeSkill?.key}
            onChange={setSelected}
            showSearch
            optionFilterProp="label"
            placeholder="스킬 선택"
            style={{ width: "100%", maxWidth: 600, marginBottom: 20 }}
            options={filteredSkills.map((s: any) => ({
              value: s.key,
              label: `${s.name} · ${entityName(s.boss_code)} · ${s.key}`,
            }))}
          />
          {activeSkill ? (
            <>
              <div className="resource-toolbar">
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    {activeSkill.name}
                  </Title>
                  <Text type="secondary">
                    {entityName(activeSkill.boss_code)} · 효과{" "}
                    {relatedEffects.length}개 · 트리거 {relatedTriggers.length}
                    개
                  </Text>
                </div>
                <Text code copyable>
                  {activeSkill.key}
                </Text>
              </div>
              <Card title="적용 효과" size="small" style={{ marginBottom: 20 }}>
                <Table
                  className="resource-table"
                  loading={loading}
                  dataSource={relatedEffects}
                  rowKey={(r: any) =>
                    String(r.id ?? JSON.stringify([r.skill, r.effect, r.order]))
                  }
                  pagination={false}
                  scroll={{ x: 650 }}
                  locale={{
                    emptyText: "이 스킬에 직접 연결된 효과가 없습니다.",
                  }}
                  columns={[
                    {
                      title: "효과",
                      render: (_: any, r: any) => (
                        <div className="resource-name">
                          <Text strong>{reference("effect", r.effect)}</Text>
                          <Text type="secondary">
                            {
                              data.effects.find((e: any) => e.key === r.effect)
                                ?.description
                            }
                          </Text>
                        </div>
                      ),
                    },
                    {
                      title: "위력",
                      dataIndex: "power",
                      width: 95,
                      render: (v: any) => v ?? "미설정",
                    },
                    {
                      title: "지속 시간",
                      dataIndex: "duration_s",
                      width: 110,
                      render: (v: any) => seconds(v, "즉시 적용"),
                    },
                    {
                      title: "추가 중첩",
                      dataIndex: "add_count",
                      width: 100,
                      render: (v: any) => v ?? "미설정",
                    },
                  ]}
                  expandable={{
                    expandedRowRender: (r: any) => (
                      <div className="resource-details">
                        <Text code copyable>
                          {r.effect}
                        </Text>
                        <Collapse
                          ghost
                          items={[
                            {
                              key: "raw",
                              label: "연결 상세 값",
                              children: (
                                <pre className="resource-json">
                                  {JSON.stringify(r, null, 2)}
                                </pre>
                              ),
                            },
                          ]}
                        />
                      </div>
                    ),
                  }}
                />
              </Card>
            </>
          ) : (
            <Empty
              description={
                loading
                  ? "연결을 불러오는 중입니다."
                  : "조건에 맞는 스킬이 없습니다."
              }
            />
          )}
        </>
      )}
      {(mode === "trigger" || activeSkill) && (
        <Card
          title={
            mode === "skill"
              ? "이 스킬을 감지·실행하는 트리거"
              : "몬스터 트리거"
          }
          size="small"
        >
          <Table
            className="resource-table"
            loading={loading}
            dataSource={relatedTriggers}
            rowKey="id"
            scroll={{ x: 700 }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (n) => `${n}개`,
            }}
            locale={{
              emptyText:
                mode === "skill"
                  ? "이 스킬을 참조하는 트리거가 없습니다."
                  : "조건에 맞는 트리거가 없습니다.",
            }}
            columns={[
              {
                title: "트리거",
                render: (_: any, t: any) => (
                  <div className="resource-name">
                    <Text strong>{t.trigger_name}</Text>
                    <Text type="secondary">{entityName(t.entity_code)}</Text>
                  </div>
                ),
              },
              {
                title: "발동 시점",
                width: 220,
                render: (_: any, t: any) => (
                  <Space direction="vertical" size={0}>
                    <Text>
                      {data.events.find(
                        (e: any) => e.event_code === t.event_type,
                      )?.event_name || t.event_type}
                    </Text>
                    {t.event_type?.startsWith("ON_SKILL_") && (
                      <Text type="secondary">
                        {reference(
                          "skill",
                          readReference(t.event_params, "skill"),
                        )}
                      </Text>
                    )}
                  </Space>
                ),
              },
              {
                title: "동작",
                width: 90,
                render: (_: any, t: any) =>
                  `${data.actions.filter((a: any) => a.trigger_id === t.id).length}개`,
              },
              {
                title: "상태",
                width: 90,
                render: (_: any, t: any) => (
                  <Tag color={t.is_enabled ? "green" : "default"}>
                    {t.is_enabled ? "활성" : "비활성"}
                  </Tag>
                ),
              },
            ]}
            expandable={{ expandedRowRender: triggerDetails }}
          />
        </Card>
      )}
    </div>
  );
};
export default RelationsTab;
